import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { decryptData } from '../utils/encryption';
import { CONSULT_URL } from '../config/constants';

const { 
  FiTrendingUp, FiDollarSign, FiUsers, FiClock, FiAward, FiHeart, 
  FiCheckCircle, FiPhone, FiBarChart2, FiPieChart, FiTarget, FiZap, 
  FiAlertCircle, FiShield, FiArrowRight, FiActivity, FiBriefcase
} = FiIcons;

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n || 0);

const capitalize = (str) => {
  if (!str) return '';
  return str.trim().split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

// Calculation engines (Sync with core logic)
const calcWebsite = (inputs) => {
  if (!inputs || !inputs.websiteInvestment || inputs.websiteInvestment < 500) return null;
  const { currentDonations, donationGrowthGoal, currentGrants, grantGrowthGoal, staffHourlyRate, currentWebsiteHours, eventAttendees, volunteerHours, websiteInvestment, organizationType, memberCount, membershipFee, memberGrowthGoal } = inputs;
  const d1 = (currentDonations || 0) * (donationGrowthGoal || 0) / 100;
  const g1 = (currentGrants || 0) * (grantGrowthGoal || 0) / 100;
  const time = (currentWebsiteHours || 0) * (staffHourlyRate || 0) * 52;
  const evt = (eventAttendees || 0) * 0.1 * 50;
  const vol = (volunteerHours || 0) * 0.2 * (staffHourlyRate || 0);
  const membershipValue = organizationType === 'association' ? (memberCount || 0) * (memberGrowthGoal / 100) * (membershipFee || 0) : 0;
  let totalAnnual = d1 + g1 + time + evt + vol + membershipValue;
  if (totalAnnual < websiteInvestment * 0.5 && (d1 > 0 || membershipValue > 0)) totalAnnual = websiteInvestment * 0.5;
  return { d1, g1, time, evt, vol, membershipValue, totalAnnual, investment: websiteInvestment };
};

const calcBranding = (inputs) => {
  if (!inputs || !inputs.brandingInvestment || inputs.brandingInvestment < 500) return null;
  const { annualOperatingBudget, socialMediaFollowers, emailListSize, monthlyWebsiteVisitors, donorRetentionGoal, brandingInvestment, brandConsistencyRating, organizationType, memberCount, membershipFee, memberGrowthGoal } = inputs;
  const cur = ((brandConsistencyRating || 5) / 10);
  const imp = Math.min(0.9, cur + 0.2);
  const donorBase = Math.max(emailListSize * 0.15, 50);
  const ret = (donorRetentionGoal / 100) * donorBase * 0.3 * 250;
  const pool = (socialMediaFollowers + (monthlyWebsiteVisitors || 0) * 12) * 0.01;
  const newD = pool * (imp - cur) * 250;
  const membershipValue = organizationType === 'association' ? (memberCount || 0) * (memberGrowthGoal / 100) * (membershipFee || 0) * 0.7 : 0;
  const annual = Math.max(ret + newD + (annualOperatingBudget || 0) * 0.01 + membershipValue, brandingInvestment * 0.5);
  return { newD, ret, membershipValue, totalAnnual: annual, investment: brandingInvestment };
};

const FullReportPage = () => {
  const location = useLocation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hash = location.hash || window.location.hash || '';
    const queryParams = new URLSearchParams(hash.split('?')[1] || '');
    
    // 1. Get Identity
    const reportName = queryParams.get('name');
    const reportId = queryParams.get('id');
    
    // 2. Get Data Blobs
    const websiteBlob = queryParams.get('data');
    const brandingBlob = queryParams.get('brandData');
    
    let wInputs = websiteBlob ? decryptData(websiteBlob) : null;
    let bInputs = brandingBlob ? decryptData(brandingBlob) : null;

    // Fallback to local storage
    if (!wInputs && !bInputs) {
      wInputs = JSON.parse(localStorage.getItem('calculator_data') || 'null');
      bInputs = JSON.parse(localStorage.getItem('calculator_brandData') || 'null');
    }

    if (!wInputs && !bInputs) {
      setLoading(false);
      return;
    }

    const wResults = calcWebsite(wInputs);
    const bResults = calcBranding(bInputs);

    // Synergy Logic
    let synergyValue = 0;
    if (wResults && bResults) {
      synergyValue = (wResults.totalAnnual + bResults.totalAnnual) * 0.15;
    }

    const totalAnnual = (wResults?.totalAnnual || 0) + (bResults?.totalAnnual || 0) + synergyValue;
    const totalInvest = (wResults?.investment || 0) + (bResults?.investment || 0);

    // Name Parsing Logic
    let name = 'Strategic Leader';
    if (reportName) {
      try { 
        name = atob(reportName); 
      } catch(e) {
        console.warn('Could not decode name from URL');
      }
    } else if (reportId) {
      try { 
        name = atob(reportId).split('@')[0]; 
      } catch(e) {
        console.warn('Could not decode id from URL');
      }
    }

    setReportData({
      firstName: capitalize(name),
      wResults,
      bResults,
      wInputs,
      bInputs,
      synergyValue,
      totalAnnual,
      totalInvest,
      roi: totalInvest > 0 ? (totalAnnual / totalInvest) * 100 : 0,
      payback: totalAnnual > 0 ? (totalInvest / (totalAnnual / 12)) : 0,
      isAssociation: (wInputs?.organizationType || bInputs?.organizationType) === 'association'
    });
    setLoading(false);
  }, [location]);

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <SafeIcon icon={FiActivity} className="text-5xl text-blue-600 animate-pulse mb-4" />
      <p className="text-gray-500 font-medium font-poppins">Synthesizing your strategic report...</p>
    </div>
  );

  if (!reportData) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center">
      <SafeIcon icon={FiAlertCircle} className="text-5xl text-red-500 mb-4" />
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Data Context Missing</h1>
      <p className="text-gray-600 max-w-md">We couldn't secure the data for this report link. Please run the calculator to generate a fresh analysis.</p>
      <a href="/" className="mt-8 bg-blue-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg">Return to Home</a>
    </div>
  );

  const { firstName, wResults, bResults, wInputs, bInputs, totalAnnual, totalInvest, roi, payback, isAssociation, synergyValue } = reportData;
  const isBoth = wResults && bResults;

  return (
    <div className="bg-[#f8fafc] min-h-screen font-poppins pb-32">
      {/* Premium Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <img src="https://wowdigital.com/logo_black.png" alt="Wow Digital" className="h-7 w-auto" />
          <div className="flex items-center gap-4">
            <div className="hidden md:flex flex-col items-end mr-4">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Analysis Prepared For</span>
              <span className="text-sm font-bold text-slate-900">{firstName}</span>
            </div>
            <a href={CONSULT_URL} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white text-[11px] font-bold px-5 py-2.5 rounded-lg shadow-blue-200 shadow-lg uppercase tracking-wider">
              Expert Review
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="bg-white border-b border-slate-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-50 -skew-x-12 translate-x-1/2" />
        <div className="container mx-auto px-6 max-w-5xl py-20 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-8 border border-blue-100">
              <SafeIcon icon={FiShield} className="text-xs" /> Strategic ROI Analysis
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mb-8 leading-tight">
              The Path to <span className="text-blue-600">{fmt(totalAnnual)}</span> in <br className="hidden md:block" /> Mission Value.
            </h1>
            <p className="text-xl text-slate-600 mb-12 max-w-3xl leading-relaxed">
              {firstName}, your organization is positioned for a <b>{fmtN(roi)}%</b> annual return on a <b>{fmt(totalInvest)}</b> strategic investment. 
              This isn't just a cost—it's a revenue engine that pays for itself in under <b>{Math.ceil(payback)} months</b>.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { label: 'Annual Impact', val: fmt(totalAnnual), sub: 'New revenue & savings', icon: FiActivity, color: 'text-blue-600' },
                { label: '3-Year Net Gain', val: fmt(totalAnnual * 3 - totalInvest), sub: 'After initial investment', icon: FiTrendingUp, color: 'text-emerald-600' },
                { label: 'Efficiency Lift', val: isBoth ? '2.4x' : '1.8x', sub: 'Projected capacity increase', icon: FiZap, color: 'text-amber-600' }
              ].map((item, i) => (
                <div key={i} className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className={`${item.color} mb-4`}><SafeIcon icon={item.icon} className="text-2xl" /></div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{item.label}</div>
                  <div className="text-3xl font-bold text-slate-900">{item.val}</div>
                  <div className="text-xs text-slate-500 mt-2">{item.sub}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </header>

      <div className="container mx-auto px-6 max-w-5xl py-20">
        <div className="grid lg:grid-cols-3 gap-16">
          <div className="lg:col-span-2 space-y-20">
            
            {/* Strategic Breakdown */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                <SafeIcon icon={FiPieChart} className="text-blue-600" />
                Impact Breakdown
              </h2>
              
              <div className="grid gap-6">
                {wResults && (
                  <div className="bg-white p-8 rounded-3xl border border-slate-200">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <div className="flex items-center gap-2 font-bold text-slate-900 text-lg mb-2">
                          <SafeIcon icon={FiBriefcase} className="text-blue-500" /> Digital Conversion Engine
                        </div>
                        <p className="text-sm text-slate-500 max-w-md">
                          By optimizing the {isAssociation ? 'member' : 'donor'} journey, we eliminate friction that currently causes abandonment.
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">{fmt(wResults.totalAnnual)}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase">Annual Yield</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-8 pt-6 border-t border-slate-50">
                      {wResults.d1 > 0 && <div className="flex flex-col"><span className="text-[10px] text-slate-400 uppercase font-bold">Fundraising Lift</span><span className="font-bold text-slate-700">{fmt(wResults.d1)}</span></div>}
                      {wResults.membershipValue > 0 && <div className="flex flex-col"><span className="text-[10px] text-slate-400 uppercase font-bold">Member Growth</span><span className="font-bold text-slate-700">{fmt(wResults.membershipValue)}</span></div>}
                      <div className="flex flex-col"><span className="text-[10px] text-slate-400 uppercase font-bold">Admin Savings</span><span className="font-bold text-slate-700">{fmt(wResults.time)}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-slate-400 uppercase font-bold">Event Revenue</span><span className="font-bold text-slate-700">{fmt(wResults.evt)}</span></div>
                    </div>
                  </div>
                )}

                {bResults && (
                  <div className="bg-white p-8 rounded-3xl border border-slate-200">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <div className="flex items-center gap-2 font-bold text-slate-900 text-lg mb-2">
                          <SafeIcon icon={FiTarget} className="text-purple-500" /> Brand Equity & Trust
                        </div>
                        <p className="text-sm text-slate-500 max-w-md">
                          A high-trust brand increases the average gift size and long-term retention of your {isAssociation ? 'members' : 'donors'}.
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-600">{fmt(bResults.totalAnnual)}</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase">Annual Yield</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-8 pt-6 border-t border-slate-50">
                      <div className="flex flex-col"><span className="text-[10px] text-slate-400 uppercase font-bold">Retention Value</span><span className="font-bold text-slate-700">{fmt(bResults.ret)}</span></div>
                      <div className="flex flex-col"><span className="text-[10px] text-slate-400 uppercase font-bold">Acquisition Impact</span><span className="font-bold text-slate-700">{fmt(bResults.newD)}</span></div>
                      {bResults.membershipValue > 0 && <div className="flex flex-col"><span className="text-[10px] text-slate-400 uppercase font-bold">Brand-Led Growth</span><span className="font-bold text-slate-700">{fmt(bResults.membershipValue)}</span></div>}
                    </div>
                  </div>
                )}

                {/* Missing Section Upsell */}
                {!isBoth && (
                  <div className="bg-slate-900 p-8 rounded-3xl text-white relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/20 blur-3xl group-hover:bg-blue-600/40 transition-all" />
                    <div className="relative z-10">
                      <h4 className="font-bold text-lg mb-2 flex items-center gap-2">
                        <SafeIcon icon={FiZap} className="text-amber-400" /> 
                        Unlock the {wResults ? 'Branding' : 'Website'} Multiplier
                      </h4>
                      <p className="text-sm text-slate-400 mb-6 max-w-md leading-relaxed">
                        Organizations that combine a high-performing website with a professional brand see a <b>25% higher conversion rate</b> than those that only fix one.
                      </p>
                      <a href="/" className="inline-flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest hover:text-blue-400 transition-colors">
                        Calculate Combined ROI <SafeIcon icon={FiArrowRight} />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Inaction Analysis */}
            <section className="bg-red-50 p-10 rounded-[2.5rem] border border-red-100">
              <h2 className="text-xl font-bold text-red-900 mb-6 flex items-center gap-2 uppercase tracking-wide">
                <SafeIcon icon={FiAlertCircle} /> The Risk of Delay
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="text-4xl font-extrabold text-red-600 mb-2">{fmt(totalAnnual / 12)}</div>
                  <div className="text-[10px] font-bold text-red-800/60 uppercase tracking-widest">Lost Opportunity Per Month</div>
                </div>
                <ul className="space-y-3">
                  {[
                    "Donor attrition increases as brand trust fades",
                    "Staff time wasted on inefficient manual workarounds",
                    "Mobile users abandoned due to poor UX",
                    "Major donors deterred by lack of digital credibility"
                  ].map((risk, idx) => (
                    <li key={idx} className="flex gap-3 text-xs text-red-800/80 font-medium">
                      <SafeIcon icon={FiX} className="mt-0.5 flex-shrink-0" /> {risk}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>

          <aside className="space-y-8">
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/50 sticky top-28">
              <h3 className="font-bold text-slate-900 mb-6 uppercase text-[10px] tracking-[0.2em] text-center">Strategic Next Steps</h3>
              
              <div className="space-y-8 mb-8">
                {[
                  { title: "Review The Numbers", desc: "A 30-minute deep dive to validate your specific assumptions and goals.", icon: FiClock },
                  { title: "Digital Blueprint", desc: "Receive a technical roadmap of the exact features needed to hit these ROI targets.", icon: FiTarget },
                  { title: "Execution Plan", desc: "A phased rollout strategy that prioritizes the highest ROI activities first.", icon: FiActivity }
                ].map((step, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="bg-blue-50 text-blue-600 w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs flex-shrink-0 border border-blue-100">
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-900 mb-1">{step.title}</h4>
                      <p className="text-[11px] text-slate-500 leading-relaxed font-medium">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a href={CONSULT_URL} target="_blank" rel="noopener noreferrer" className="block w-full bg-blue-600 text-white text-center text-xs font-bold py-4 rounded-2xl shadow-lg shadow-blue-200/50 hover:bg-blue-700 hover:scale-[1.02] transition-all uppercase tracking-widest">
                Book Strategy Call
              </a>
              <p className="text-[9px] text-slate-400 text-center mt-4 font-bold tracking-widest uppercase">Limited Availability This Month</p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-3xl text-white">
              <SafeIcon icon={FiAward} className="text-3xl text-amber-400 mb-4" />
              <h4 className="font-bold mb-2">Our Results Guarantee</h4>
              <p className="text-[11px] text-slate-400 leading-relaxed font-medium">
                We specialize in high-conversion digital ecosystems for growing Nonprofits and Associations. If we don't see a clear path to at least a 3x return, we won't take the project.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const FiX = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

export default FullReportPage;